import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import { CATEGORY_FILTER_DRAWER_ALLOWED_FILTERS } from '@app/src/components/CreateNewRequestModal/Common/SelectFilterStep'
import Table from '@app/src/components/Table'
import CreationModalProgressContextProvider from '@app/src/context/CreationModalProgressContextProvider'
import { useDialogState } from '@app/src/hooks/mui-hooks'
import usePaginationWithQuery from '@app/src/hooks/paginationWithQuery'
import ConfigurationCollection from '@app/src/pages/Configurations/ConfigurationCollection'
import CreateAnswerFlagModal from '@app/src/pages/Configurations/ConfigurationsPages/AnswerFlag/CreateAnswerFlagModal'
import Filters from '@app/src/pages/ResourceCollection/Filters/Filters'
import type { AnswerFlagRule } from '@app/src/types/answerFlags'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import AnswerFlagTableHeader from './AnswerFlagTableHeader'
import AnswerFlagTableRow from './AnswerFlagTableRow'

const ANSWER_FLAG_RULE_INCLUDES = ['question', 'conditions']

function AnswerFlagConfiguration(): React.ReactElement {
  const { formatMessage } = useIntl()
  const [page, pageSize, setPage, setPageSize] = usePaginationWithQuery()
  const [isAnswerFlagDialogOpen, openAnswerFlagDialog, closeAnswerFlagDialog] = useDialogState(false)
  const [editedRule, setEditedRule] = useState<AnswerFlagRule | undefined>(undefined)

  const { items, count, isLoading, isError } = useFetchCollectionWithPost<AnswerFlagRule>({
    key: FetchKey.AnswerFlagRuleCollection,
    endpoint: endpoints.answerFlagRuleCollection,
    payload: {
      filter: [],
      include: ANSWER_FLAG_RULE_INCLUDES,
      pagination: { pageNumber: page, itemsPerPage: pageSize },
    },
  })

  const handleEdit = (rule: AnswerFlagRule) => {
    setEditedRule(rule)
    openAnswerFlagDialog()
  }

  const handleCloseModal = () => {
    setEditedRule(undefined)
    closeAnswerFlagDialog()
  }

  const handleCreateNew = () => {
    setEditedRule(undefined)
    openAnswerFlagDialog()
  }

  return (
    <>
      <ConfigurationCollection
        actionButtons={[
          {
            label: formatMessage({ id: 'resourceCollections.general.create' }),
            variant: 'contained',
            onClick: handleCreateNew,
          },
        ]}
      >
        <Table<AnswerFlagRule>
          RowComponent={({ row }) => <AnswerFlagTableRow row={row} onEdit={handleEdit} />}
          HeaderComponent={AnswerFlagTableHeader}
          data={items}
          isLoading={isLoading}
          count={count}
          isError={isError}
          page={page}
          pageSize={pageSize}
          setPage={setPage}
          setPageSize={setPageSize}
        />
      </ConfigurationCollection>
      <CreationModalProgressContextProvider>
        <Filters allowedFilters={CATEGORY_FILTER_DRAWER_ALLOWED_FILTERS}>
          {({ clearQueryFilter }) => (
            <CreateAnswerFlagModal
              onClose={() => {
                clearQueryFilter()
                handleCloseModal()
              }}
              open={isAnswerFlagDialogOpen}
              editedRule={editedRule}
            />
          )}
        </Filters>
      </CreationModalProgressContextProvider>
    </>
  )
}

export default AnswerFlagConfiguration
